import { createStudent, createGoal, createSettings } from './dataModels';

const STORAGE_KEYS = {
  STUDENTS: 'iep-students',
  GOALS: 'iep-goals',
  SETTINGS: 'iep-settings',
  LAST_BACKUP: 'iep-lastBackup'
};

const getFromStorage = (key, defaultValue = []) => {
  try {
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : defaultValue;
  } catch (error) {
    console.error(`Error reading from localStorage for key ${key}:`, error);
    return defaultValue;
  }
};

const saveToStorage = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
    localStorage.setItem(STORAGE_KEYS.LAST_BACKUP, new Date().toISOString());
    return true;
  } catch (error) {
    console.error(`Error saving to localStorage for key ${key}:`, error);
    return false;
  }
};

export const studentStorage = {
  getAll: () => getFromStorage(STORAGE_KEYS.STUDENTS, []),
  
  getById: (id) => {
    const students = getFromStorage(STORAGE_KEYS.STUDENTS, []);
    return students.find(student => student.id === id) || null;
  },
  
  add: (studentData) => {
    const students = getFromStorage(STORAGE_KEYS.STUDENTS, []);
    const newStudent = createStudent(studentData);
    students.push(newStudent);
    saveToStorage(STORAGE_KEYS.STUDENTS, students);
    return newStudent;
  },
  
  update: (id, updates) => {
    const students = getFromStorage(STORAGE_KEYS.STUDENTS, []);
    const index = students.findIndex(student => student.id === id);
    
    if (index !== -1) {
      students[index] = {
        ...students[index],
        ...updates,
        updatedAt: new Date().toISOString()
      };
      saveToStorage(STORAGE_KEYS.STUDENTS, students);
      return students[index];
    }
    return null;
  },
  
  delete: (id) => {
    const students = getFromStorage(STORAGE_KEYS.STUDENTS, []);
    const filteredStudents = students.filter(student => student.id !== id);
    
    if (filteredStudents.length !== students.length) {
      saveToStorage(STORAGE_KEYS.STUDENTS, filteredStudents);
      
      const goals = getFromStorage(STORAGE_KEYS.GOALS, []);
      const filteredGoals = goals.filter(goal => goal.studentId !== id);
      saveToStorage(STORAGE_KEYS.GOALS, filteredGoals);
      
      return true;
    }
    return false;
  }
};

export const goalStorage = {
  getAll: () => getFromStorage(STORAGE_KEYS.GOALS, []),
  
  getById: (id) => {
    const goals = getFromStorage(STORAGE_KEYS.GOALS, []);
    return goals.find(goal => goal.id === id) || null;
  },
  
  getByStudentId: (studentId) => {
    const goals = getFromStorage(STORAGE_KEYS.GOALS, []);
    return goals.filter(goal => goal.studentId === studentId);
  },
  
  add: (goalData) => {
    const goals = getFromStorage(STORAGE_KEYS.GOALS, []);
    const newGoal = createGoal(goalData);
    goals.push(newGoal);
    saveToStorage(STORAGE_KEYS.GOALS, goals);
    
    const students = getFromStorage(STORAGE_KEYS.STUDENTS, []);
    const student = students.find(s => s.id === newGoal.studentId);
    if (student && !student.goals.includes(newGoal.id)) {
      student.goals.push(newGoal.id);
      student.updatedAt = new Date().toISOString();
      saveToStorage(STORAGE_KEYS.STUDENTS, students);
    }
    
    return newGoal;
  },
  
  update: (id, updates) => {
    const goals = getFromStorage(STORAGE_KEYS.GOALS, []);
    const index = goals.findIndex(goal => goal.id === id);
    
    if (index !== -1) {
      goals[index] = {
        ...goals[index],
        ...updates,
        updatedAt: new Date().toISOString()
      };
      saveToStorage(STORAGE_KEYS.GOALS, goals);
      return goals[index];
    }
    return null;
  },
  
  delete: (id) => {
    const goals = getFromStorage(STORAGE_KEYS.GOALS, []);
    const filteredGoals = goals.filter(goal => goal.id !== id);
    
    if (filteredGoals.length !== goals.length) {
      saveToStorage(STORAGE_KEYS.GOALS, filteredGoals);
      
      const students = getFromStorage(STORAGE_KEYS.STUDENTS, []);
      students.forEach(student => {
        const goalIndex = student.goals.indexOf(id);
        if (goalIndex !== -1) {
          student.goals.splice(goalIndex, 1);
          student.updatedAt = new Date().toISOString();
        }
      });
      saveToStorage(STORAGE_KEYS.STUDENTS, students);
      
      return true;
    }
    return false;
  },
  
  addProgressEntry: (goalId, progressData) => {
    const goals = getFromStorage(STORAGE_KEYS.GOALS, []);
    const goal = goals.find(g => g.id === goalId);
    
    if (goal) {
      goal.progressData.push(progressData);
      goal.updatedAt = new Date().toISOString();
      saveToStorage(STORAGE_KEYS.GOALS, goals);
      return goal;
    }
    return null;
  }
};

export const settingsStorage = {
  get: () => {
    const defaultSettings = createSettings();
    return getFromStorage(STORAGE_KEYS.SETTINGS, defaultSettings);
  },
  
  update: (updates) => {
    const currentSettings = getFromStorage(STORAGE_KEYS.SETTINGS, createSettings());
    const newSettings = { ...currentSettings, ...updates };
    saveToStorage(STORAGE_KEYS.SETTINGS, newSettings);
    return newSettings;
  },
  
  reset: () => {
    const defaultSettings = createSettings();
    saveToStorage(STORAGE_KEYS.SETTINGS, defaultSettings);
    return defaultSettings;
  }
};

export const dataOperations = {
  exportData: () => {
    const students = getFromStorage(STORAGE_KEYS.STUDENTS, []);
    const goals = getFromStorage(STORAGE_KEYS.GOALS, []);
    const settings = getFromStorage(STORAGE_KEYS.SETTINGS, createSettings());
    
    return {
      students,
      goals,
      settings,
      exportDate: new Date().toISOString(),
      version: '1.0'
    };
  },
  
  importData: (data) => {
    try {
      if (data.students) {
        saveToStorage(STORAGE_KEYS.STUDENTS, data.students);
      }
      if (data.goals) {
        saveToStorage(STORAGE_KEYS.GOALS, data.goals);
      }
      if (data.settings) {
        saveToStorage(STORAGE_KEYS.SETTINGS, data.settings);
      }
      return true;
    } catch (error) {
      console.error('Error importing data:', error);
      return false;
    }
  },
  
  clearAllData: () => {
    try {
      localStorage.removeItem(STORAGE_KEYS.STUDENTS);
      localStorage.removeItem(STORAGE_KEYS.GOALS);
      localStorage.removeItem(STORAGE_KEYS.SETTINGS);
      localStorage.removeItem(STORAGE_KEYS.LAST_BACKUP);
      return true;
    } catch (error) {
      console.error('Error clearing data:', error);
      return false;
    }
  },
  
  getLastBackupDate: () => {
    return getFromStorage(STORAGE_KEYS.LAST_BACKUP, null);
  }
};

export const seedSampleData = () => {
  const existingStudents = getFromStorage(STORAGE_KEYS.STUDENTS, []);
  
  if (existingStudents.length === 0) {
    const sampleStudents = [
      createStudent({
        firstName: 'Emma',
        lastName: 'Johnson',
        grade: '3rd',
        disabilityCategory: 'Specific Learning Disability',
        iepStartDate: '2024-08-15',
        iepEndDate: '2025-08-14',
        caseManager: 'Ms. Smith',
        parentContact: {
          name: 'Sarah Johnson',
          email: 'sarah.johnson@email.com',
          phone: '(555) 123-4567'
        }
      }),
      createStudent({
        firstName: 'Marcus',
        lastName: 'Williams',
        grade: '5th',
        disabilityCategory: 'Autism Spectrum Disorder',
        iepStartDate: '2024-09-01',
        iepEndDate: '2025-08-31',
        caseManager: 'Mr. Davis',
        parentContact: {
          name: 'Michael Williams',
          email: 'mike.williams@email.com',
          phone: '(555) 987-6543'
        }
      })
    ];
    
    saveToStorage(STORAGE_KEYS.STUDENTS, sampleStudents);
    
    const sampleGoals = [
      createGoal({
        studentId: sampleStudents[0].id,
        domain: 'Academic',
        description: 'Emma will read 50 sight words with 80% accuracy in 4 out of 5 trials',
        measurementMethod: 'Direct observation and data collection',
        targetCriteria: '80% accuracy over 5 consecutive sessions',
        reviewFrequency: 'Monthly',
        status: 'In Progress',
        progressData: [
          { date: '2024-09-15', value: 65, notes: 'Good progress on common words', dataType: 'Percentage' },
          { date: '2024-10-15', value: 72, notes: 'Improvement noted in retention', dataType: 'Percentage' }
        ]
      }),
      createGoal({
        studentId: sampleStudents[1].id,
        domain: 'Social',
        description: 'Marcus will initiate appropriate social interactions with peers during recess',
        measurementMethod: 'Behavioral observation checklist',
        targetCriteria: '3 appropriate interactions per recess period',
        reviewFrequency: 'Weekly',
        status: 'In Progress',
        progressData: [
          { date: '2024-09-20', value: 40, notes: 'Initiated 2 interactions', dataType: 'Frequency' },
          { date: '2024-09-27', value: 60, notes: 'Positive peer responses noted', dataType: 'Frequency' }
        ]
      })
    ];
    
    saveToStorage(STORAGE_KEYS.GOALS, sampleGoals);
    
    sampleStudents[0].goals = [sampleGoals[0].id];
    sampleStudents[1].goals = [sampleGoals[1].id];
    saveToStorage(STORAGE_KEYS.STUDENTS, sampleStudents);
  }
};